import type { SkuAnnotation, StyleRun } from '@rta/rta-blueprint-library';

import { applyStyleRun, nullifyStyleRunPatch, type StyleRunPatch } from './apply-style-run';

/** Style properties with no box-level field: a whole-box change to one of these lands as a run spanning the whole text */
const RUN_ONLY_PROPERTIES: Array<keyof StyleRunPatch> = ['textDecoration', 'backgroundColor'];

const toRunOnlyPatch = (patch: StyleRunPatch): StyleRunPatch | null => {
	const runOnly: Record<string, unknown> = {};
	RUN_ONLY_PROPERTIES.forEach((property) => {
		const value = patch[property];
		if (typeof value === 'undefined' || value === null) return;
		runOnly[property] = value;
	});
	return Object.keys(runOnly).length > 0 ? runOnly as StyleRunPatch : null;
};

export type ApplyAnnotationBoxPatchParams = {
	annotation: SkuAnnotation;
	patch: StyleRunPatch;
	/** The runs the caller holds: an editor's uncommitted draft, or the annotation's resolved runs */
	runs: Array<StyleRun>;
	/** Length of the text those runs span: an editor's draft text, or the annotation's own */
	textLength: number;
};

/**
 * Whole-box text formatting: writes the box-level scalars and RETURNS the runs the caller should now hold,
 * with the same properties stripped off every one of them -- otherwise an already-formatted range ignores
 * the change. `backgroundColor` is the per-range highlight and never the box fill, which only
 * `setAnnotationFill` writes.
 */
export const applyAnnotationBoxPatch = (params: ApplyAnnotationBoxPatchParams): Array<StyleRun> => {
	const { annotation, patch, runs, textLength } = params;
	const { color, fontWeight, fontStyle, fontFamily, fontSize } = patch;

	if (typeof color !== 'undefined') annotation.textColor = color ?? '';
	if (typeof fontWeight !== 'undefined') annotation.fontWeight = fontWeight ?? 'normal';
	if (typeof fontStyle !== 'undefined') annotation.fontStyle = fontStyle ?? 'normal';
	if (typeof fontFamily !== 'undefined') annotation.fontFamily = fontFamily ?? 'Arial';
	if (typeof fontSize === 'number') annotation.fontSize = fontSize;
	if (textLength === 0) return runs;

	const cleared = applyStyleRun({ runs, start: 0, end: textLength, patch: nullifyStyleRunPatch(patch) });
	const runOnly = toRunOnlyPatch(patch);
	return runOnly ? applyStyleRun({ runs: cleared, start: 0, end: textLength, patch: runOnly }) : cleared;
};
